import { useLoyaltyBalance, useOrderPreview } from '@order-up/client/vite';
import { Award } from 'lucide-react';
import { useEffect, useMemo } from 'react';

/**
 * Customer Loyalty v1 — B8: checkout redemption UX.
 *
 * Renders ONLY when:
 *   - the `initiative.customer_loyalty` flag is on (ships dark otherwise), AND
 *   - the checkout customer is enrolled (has a `customers/{uid}` identity that
 *     produced a loyalty balance), AND
 *   - that balance is > 0.
 *
 * SERVER IS AUTHORITATIVE. The customer chooses an INTEGER number of points to
 * redeem; the component lifts that intent up via `onRedeemPointsChange` and the
 * parent re-`previewOrder(cartId, tip, redeemPoints)`. The applied discount we
 * show is whatever the server echoed in `preview.loyaltyDiscount` — never a
 * client-computed dollar amount. `centsPerPoint` (used only for the slider's
 * "redeemable value" hint + the value-per-step label) is itself *derived from
 * the server*: a single read-only "rate probe" preview at `redeemPoints =
 * balance` whose echoed `loyaltyDiscount.cents / points` is the true rate. That
 * probe uses a distinct preview query key, so it never perturbs the parent's
 * amount-locked `expectedTotal`.
 *
 * Why the auth UID is the loyalty key: the server's `/preview` + `/checkout`
 * resolve the redeemable identity read-only by the checkout uid
 * (`getCustomerByUid(checkoutUid)`), and the self-access balance endpoint keys
 * on that same uid. So fetching `useLoyaltyBalance(..., customerUid)` with the
 * current auth uid shows exactly the balance the server will debit.
 */

export interface LoyaltyRedeemProps {
    /** Tenant Firestore doc ID (from the resolved restaurant page). */
    tenantDocId?: string;
    /** Restaurant Firestore doc ID (from the resolved restaurant page). */
    restaurantDocId?: string;
    /** Current authenticated customer UID (anonymous/phone session). */
    customerUid?: string;
    /** Cart id for the rate-probe preview. */
    cartId?: number | null;
    /** Tip (dollars) the parent is previewing with — keeps the probe basis aligned. */
    tipAmount: number;
    /** Current redemption intent (points). Owned by the parent (drives its preview). */
    redeemPoints: number;
    /** Lift the chosen integer point count to the parent (which re-previews). */
    onRedeemPointsChange: (points: number) => void;
    /**
     * The SERVER-DERIVED discount echoed by the parent's live preview, if any.
     * `points` is what will burn, `cents` is the integer-cent discount already
     * subtracted from the preview total. Authoritative for the "−$X" line.
     */
    loyaltyDiscount?: { points: number; cents: number };
    /**
     * Customer loyalty is live for this location — the slug payload's
     * `customerLoyalty` (`initiative.customer_loyalty` AND an active program,
     * evaluated by the API). Read from there rather than
     * `/feature-flags/evaluate`, which refuses a guest session. Defaults to
     * off, so the slot is invisible until proven on.
     */
    loyaltyEnabled?: boolean;
}

export default function LoyaltyRedeem({
    tenantDocId,
    restaurantDocId: locationDocId,
    customerUid,
    cartId,
    tipAmount,
    redeemPoints,
    onRedeemPointsChange,
    loyaltyDiscount,
    loyaltyEnabled = false,
}: LoyaltyRedeemProps) {
    const flagEnabled = loyaltyEnabled;

    // The customer's OWN balance (self-access endpoint). `enabled` is gated on
    // the flag + identity inside the hook (all three params required), so this
    // is a no-op query when the feature is dark or the customer is anonymous
    // without an enrolled identity.
    const { data: balanceData } = useLoyaltyBalance(
        flagEnabled ? tenantDocId : undefined,
        flagEnabled ? locationDocId : undefined,
        flagEnabled ? customerUid : undefined,
    );

    const balance = Math.max(0, Math.floor(balanceData?.balance ?? 0));
    const isEnrolledWithPoints = flagEnabled && balance > 0;

    // ── Server-derived per-point value (cents) ──────────────────────────────
    // Read-only "rate probe": preview the MAX redemption (redeemPoints =
    // balance). The server clamps to the subtotal cap and echoes
    // loyaltyDiscount.{points,cents}; cents/points is the exact centsPerPoint.
    // Distinct query key (its own redeemPoints) → never touches the parent's
    // locked preview. Only runs when there's a positive balance to value.
    const { data: probePreview } = useOrderPreview(
        isEnrolledWithPoints ? (cartId ?? null) : null,
        tipAmount,
        isEnrolledWithPoints ? balance : 0,
    );

    const centsPerPoint = useMemo(() => {
        const d = probePreview?.loyaltyDiscount;
        if (d && d.points > 0 && d.cents > 0) {
            return d.cents / d.points;
        }
        return 0;
    }, [probePreview]);

    // The most points that actually redeem on THIS order (subtotal-capped),
    // surfaced by the probe. The slider can never exceed this — it mirrors the
    // server clamp so the chosen value always fully applies (no silent drop).
    const maxRedeemablePoints = useMemo(() => {
        const probePoints = probePreview?.loyaltyDiscount?.points;
        // Until the probe resolves, fall back to the raw balance so the control
        // is usable; the server still re-clamps, and the displayed −$ line uses
        // the authoritative live echo.
        return typeof probePoints === 'number' ? Math.min(balance, probePoints) : balance;
    }, [probePreview, balance]);

    // If the order shrinks (e.g. subtotal cap drops) below the current intent,
    // pull the intent back down so we never submit an over-cap request.
    useEffect(() => {
        if (isEnrolledWithPoints && redeemPoints > maxRedeemablePoints) {
            onRedeemPointsChange(maxRedeemablePoints);
        }
    }, [isEnrolledWithPoints, redeemPoints, maxRedeemablePoints, onRedeemPointsChange]);

    if (!isEnrolledWithPoints) {
        return null;
    }

    // Authoritative applied discount (cents) from the parent's LIVE preview when
    // points are being redeemed; otherwise the projected value from the rate
    // probe (display-only, never charged).
    const appliedCents =
        redeemPoints > 0 && loyaltyDiscount && loyaltyDiscount.cents > 0
            ? loyaltyDiscount.cents
            : Math.round(redeemPoints * centsPerPoint);

    const redeemableValueDollars = (maxRedeemablePoints * centsPerPoint) / 100;
    const appliedDollars = appliedCents / 100;

    const handleSlider = (value: number) => {
        const clamped = Math.max(0, Math.min(Math.floor(value), maxRedeemablePoints));
        onRedeemPointsChange(clamped);
    };

    return (
        <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2">
                    <Award className="h-5 w-5 text-gray-700" aria-hidden />
                    <div>
                        <p className="font-semibold text-gray-900">Redeem rewards</p>
                        <p className="text-xs text-gray-500">
                            {balance.toLocaleString()} points available
                            {centsPerPoint > 0 && (
                                <> · worth up to ${redeemableValueDollars.toFixed(2)} on this order</>
                            )}
                        </p>
                    </div>
                </div>
                {redeemPoints > 0 && (
                    <button
                        type="button"
                        onClick={() => onRedeemPointsChange(0)}
                        className="text-xs font-medium text-gray-600 underline hover:text-gray-900"
                    >
                        Clear
                    </button>
                )}
            </div>

            {/* Stepper + slider — points only. The integer intent is lifted to the
                parent, which re-previews so the total + amount-lock update together. */}
            <div className="mt-4 flex items-center gap-3">
                <button
                    type="button"
                    aria-label="Redeem fewer points"
                    disabled={redeemPoints <= 0}
                    onClick={() => handleSlider(redeemPoints - 1)}
                    className="h-9 w-9 shrink-0 rounded-lg border border-gray-300 text-lg font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    −
                </button>
                <input
                    type="range"
                    min={0}
                    max={maxRedeemablePoints}
                    step={1}
                    value={Math.min(redeemPoints, maxRedeemablePoints)}
                    onChange={(e) => handleSlider(Number(e.target.value))}
                    aria-label="Points to redeem"
                    className="flex-1 accent-gray-900"
                />
                <button
                    type="button"
                    aria-label="Redeem more points"
                    disabled={redeemPoints >= maxRedeemablePoints}
                    onClick={() => handleSlider(redeemPoints + 1)}
                    className="h-9 w-9 shrink-0 rounded-lg border border-gray-300 text-lg font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    +
                </button>
            </div>

            <div className="mt-3 flex items-center justify-between text-sm">
                <span className="text-gray-600">
                    Redeeming <span className="font-semibold text-gray-900">{redeemPoints.toLocaleString()}</span>{' '}
                    {redeemPoints === 1 ? 'point' : 'points'}
                </span>
                <span className={`font-semibold ${appliedCents > 0 ? 'text-green-700' : 'text-gray-400'}`}>
                    −${appliedDollars.toFixed(2)} loyalty
                </span>
            </div>
        </div>
    );
}
